import {
  getWindowsCommsDefaultMicGate,
  useIsAppsWindowsCommsDefaultMicDesktopEnabled,
  useIsAppsWindowsSystemAudioCaptureDesktopEnabled,
} from '@/features/flags';
import { type Dispatch, type RootState } from '@/store';
import {
  buildDisplayDeviceList,
  type DisplayAudioDevice,
  type SystemWideAudioOption,
} from '@/tauri/audioDeviceUtils';
import { useTranslations } from '@heidi/runtime/i18n';
import { isWindows } from '@heidi/utils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

type AudioDeviceSelectorResult = {
  // Raw devices from store
  tauriInputDevices: RootState['scribeRecordModel']['tauriInputDevices'];
  tauriOutputDevices: RootState['scribeRecordModel']['tauriOutputDevices'];
  tauriInputDeviceSelected: RootState['scribeRecordModel']['tauriInputDeviceSelected'];
  tauriOutputDeviceSelected: RootState['scribeRecordModel']['tauriOutputDeviceSelected'];

  // Display-ready data for UI
  inputDisplayDevices: DisplayAudioDevice[];
  inputSelectedLabel: string;
  outputDisplayDevices: DisplayAudioDevice[];
  outputSelectedLabel: string;

  // Grouped output devices (for split Speakers / Apps UI)
  outputSpeakerDevices: DisplayAudioDevice[];
  outputAppDevices: DisplayAudioDevice[];
};
/** Refresh desktop audio devices without adding new Rematch coupling to callers. */
export function useRefreshTauriAudioDevices(): () => Promise<void> {
  const dispatch = useDispatch<Dispatch>();
  return useCallback(
    () => dispatch.scribeRecordModel.getTauriDevices().then(() => undefined),
    [dispatch]
  );
}

/** Narrow read of the selected session mic name. */
export function useSelectedTauriInputDeviceName(): string | undefined {
  return useSelector(
    (state: RootState) => state.scribeRecordModel.tauriInputDeviceSelected?.name
  );
}

/**
 * Custom hook that provides audio device data with display-ready labels.
 * Combines store state with localized System Default labels.
 */
export function useAudioDeviceSelector(): AudioDeviceSelectorResult {
  const { t } = useTranslations();
  const intl = useIntl();
  const systemDefaultLabel = t('common.systemDefault');
  const getDeviceFallbackLabel = (index: number) =>
    t('desktopMicrophone.deviceFallback', { number: index + 1 });

  // "System audio" (whole-system capture) entry for the speaker dropdown —
  // Windows desktop only, behind the kill-switch flag.
  const systemAudioCaptureEnabled = useIsAppsWindowsSystemAudioCaptureDesktopEnabled();
  const systemWideOption: SystemWideAudioOption | undefined =
    systemAudioCaptureEnabled && isWindows
      ? {
          label: intl.formatMessage({
            defaultMessage: 'System audio',
            id: '9Koq3wLStc',
            description:
              'Speaker dropdown entry for Windows whole-system audio capture (records every app, not one output device)',
          }),
          caption: intl.formatMessage({
            defaultMessage: 'Captures all audio playing on this computer',
            id: '/X2s19LOgX',
            description:
              'Disclosure line under the "System audio" speaker dropdown entry explaining that whole-system capture records all computer audio',
          }),
        }
      : undefined;

  const {
    tauriInputDevices,
    tauriOutputDevices,
    tauriInputDeviceSelected,
    tauriOutputDeviceSelected,
  } = useSelector((state: RootState) => state.scribeRecordModel);

  // Keep the "System Default" row present on comms-default-only machines: the
  // hook re-renders when the flag flips post-hydration, and the tri-state gate
  // covers the pre-hydration window ('unknown' must not hide the selected
  // sentinel's menu item).
  const commsDefaultMicEnabled = useIsAppsWindowsCommsDefaultMicDesktopEnabled();
  const commsDefaultEligible =
    commsDefaultMicEnabled || getWindowsCommsDefaultMicGate() !== 'off';

  // Build display-ready device lists
  const {
    displayDevices: inputDisplayDevices,
    selectedDisplayLabel: inputSelectedLabel,
  } = buildDisplayDeviceList(
    tauriInputDevices ?? [],
    tauriInputDeviceSelected,
    systemDefaultLabel,
    getDeviceFallbackLabel,
    undefined,
    commsDefaultEligible
  );

  const {
    displayDevices: outputDisplayDevices,
    selectedDisplayLabel: outputSelectedLabel,
    speakerDevices: outputSpeakerDevices,
    appDevices: outputAppDevices,
  } = buildDisplayDeviceList(
    tauriOutputDevices ?? [],
    tauriOutputDeviceSelected,
    systemDefaultLabel,
    getDeviceFallbackLabel,
    systemWideOption
  );

  return {
    // Raw devices (for callbacks that need to find device by name)
    tauriInputDevices,
    tauriOutputDevices,
    tauriInputDeviceSelected,
    tauriOutputDeviceSelected,

    // Display-ready data
    inputDisplayDevices,
    inputSelectedLabel,
    outputDisplayDevices,
    outputSelectedLabel,

    // Grouped
    outputSpeakerDevices,
    outputAppDevices,
  };
}
